import { useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  Button,
  ChevronRight,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@mesh.ai/rabbit';
import { useSelectReviewCreationCalibrationAndDeliverables } from 'reducers/reviewCreationV1Slice';

import { ViewStar, Outcome as OutcomeIcon } from 'assets/assetsV1/iconComponents';

import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const ScoreRatingIcon = () => (
  <Flex
    h="20px"
    w="20px"
    minW="20px"
    mr="8px"
    borderRadius="50%"
    bg="#E1F7E2"
    alignItems="center"
    justifyContent="center"
  >
    <ViewStar boxSize="10px" color="#266F2A" />
  </Flex>
);

const OutcomeOptionsIcon = () => (
  <Flex
    h="20px"
    w="20px"
    minW="20px"
    mr="8px"
    borderRadius="50%"
    bg="#F0EEF9"
    alignItems="center"
    justifyContent="center"
  >
    <OutcomeIcon boxSize="10px" color="#7451B0" />
  </Flex>
);

const EmptyBox = ({ title, onClick }: { title: string; onClick?: () => void }) => (
  <Box p="10px 0" borderBottom="1px dashed" borderColor="text.g3" cursor="pointer" onClick={onClick}>
    <TextBodyMedium color="text.g3">{title}</TextBodyMedium>
  </Box>
);

const MaxScore = ({
  maxScore,
  onClick,
}: {
  maxScore?: number;

  onClick?: () => void;
}) => {
  if ([null, undefined].includes(maxScore)) {
    return <EmptyBox title="No max score" onClick={onClick} />;
  }
  return (
    <Flex p="10px 0" borderBottom="1px dashed" borderColor="#0D819F" cursor="pointer" onClick={onClick}>
      <ScoreRatingIcon />
      <TextBodyMedium color="#0D819F">Max score: {maxScore}</TextBodyMedium>
    </Flex>
  );
};

const Outcome = ({ outcome, onClick }: { outcome: boolean; onClick?: () => void }) => {
  if (!outcome) {
    return <EmptyBox title="No outcome" onClick={onClick} />;
  }
  return (
    <Flex p="10px 0" borderBottom="1px dashed" borderColor="#0D819F" cursor="pointer" onClick={onClick}>
      <OutcomeOptionsIcon />
      <TextBodyMedium color="#0D819F">Includes outcome</TextBodyMedium>
    </Flex>
  );
};

type Props = {
  onChangeClick: () => void;
};

const FinalScoreOutcomeGrid = ({ onChangeClick }: Props) => {
  const { calibrationQuestion, outcomeQuestion } = useSelectReviewCreationCalibrationAndDeliverables();

  const { maxRating } = calibrationQuestion ?? {};

  const calibrationTooltip = useMemo(() => {
    if (calibrationQuestion?.answerType === 'DROP_DOWN') {
      const { answerOptions } = calibrationQuestion;
      return (
        <Box>
          {answerOptions?.map((option) => (
            <Flex key={option.score} alignItems="center">
              <TextLabel color="white">{option.score - 1}</TextLabel>
              <Box mx="4px">-</Box>
              <TextLabel color="white">{option.score}</TextLabel>
              <Box mx="4px">-</Box>
              <TextLabel color="white">{option.description}</TextLabel>
            </Flex>
          ))}
        </Box>
      );
    }
    return null;
  }, [calibrationQuestion]);

  const outcomeTooltip = useMemo(() => {
    if (outcomeQuestion) {
      const { answerOptions } = outcomeQuestion;
      return (
        <Box>
          {answerOptions?.map((option) => (
            <Flex key={option.score} alignItems="center">
              <TextLabel color="white">{option.score - 1}</TextLabel>
              <Box mx="4px">-</Box>
              <TextLabel color="white">{option.score}</TextLabel>
              <Box mx="4px">-</Box>
              <TextLabel color="white">{option.description}</TextLabel>
            </Flex>
          ))}
        </Box>
      );
    }
    return null;
  }, [outcomeQuestion]);

  return (
    <Flex alignItems="center">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <MaxScore onClick={onChangeClick} maxScore={maxRating} />
          </TooltipTrigger>
          <TooltipPortal>
            {calibrationTooltip !== null && <TooltipContent>{calibrationTooltip}</TooltipContent>}
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
      <Box mx="8px">•</Box>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Outcome outcome={!!outcomeQuestion} onClick={onChangeClick} />
          </TooltipTrigger>
          <TooltipPortal>{outcomeTooltip !== null && <TooltipContent>{outcomeTooltip}</TooltipContent>}</TooltipPortal>
        </Tooltip>
      </TooltipProvider>
      {/* <Box mx="8px">•</Box>
      <EmptyBox title="No grid" onClick={onChangeClick}  /> */}
      <Button
        variant="text"
        style={{
          marginLeft: '8px',
        }}
        iconRight={ChevronRight}
        onClick={onChangeClick}
      >
        Change
      </Button>
    </Flex>
  );
};

export { FinalScoreOutcomeGrid };
